import { config, REPRO_KIND, reproKindOf } from '../pedigree/config';

// Which single-node context-menu actions apply to a reproductive node (Bennett 2022),
// and the noun used to label the node-typed actions (Delete/Hide/Note).
//
// Ordinary (non-repro) nodes — and EVERY node when ENABLE_REPRO_NODES is off — get the
// full menu with "Person" labels, so existing pedigrees are unchanged. Pregnancies and
// gamete donors get a restricted menu: no Add/Set Relation, Proxy, Gender, Adopted,
// Fertility, Make/Remove Proband, or Migrate (none of those apply to a conception or a
// gamete source). A donor additionally has NO Reproductive submenu (no donor actions
// yet); a pregnancy keeps it (edit outcome + add donors).
//
// NOTE: the person-scoped actions (Person Note, Edit Text, Hide, Reset Position, Delete)
// are ALSO gated off for a repro node — `del`/`note`/`hide` are all `!isRestricted`. An
// earlier version of this comment claimed they stayed for every node, relabelled via
// `nounLabel`; that was never what the code did, and it misled anyone reasoning about
// Delete from the comment alone (corrected under TGCLOUD-1644). `nounLabel` is still the
// right thing to render with, because the record-based equivalents under the Reproductive
// submenu use it. Surrogates are deferred (PLAN §4.4/§20) and treated as ordinary here
// until their wiring is signed off.
export function reproMenuPolicy(node) {
  const kind = config.ENABLE_REPRO_NODES ? reproKindOf(node) : null;
  const isPregnancy = kind === REPRO_KIND.pregnancy;
  const isDonor = kind === REPRO_KIND.gameteDonor;
  const isRestricted = isPregnancy || isDonor;

  return {
    nounLabel: isPregnancy ? 'Pregnancy' : isDonor ? 'Donor' : 'Person',
    isRepro: isRestricted,
    allow: {
      addRelation: !isRestricted,
      setRelation: !isRestricted,
      proxy: !isRestricted,
      gender: !isRestricted,
      adopted: !isRestricted,
      fertility: !isRestricted,
      makeProband: !isRestricted,
      migrate: !isRestricted,
      // The generic person actions are member-based, so they do NOT apply to a derived
      // reproductive node (a pregnancy/donor is not a member). A derived node's only
      // actions are the record-based ones under the Reproductive submenu (edit / delete).
      // (Record-based Note + Hide land in a later phase.)
      note: !isRestricted,
      editText: !isRestricted,
      hide: !isRestricted,
      resetPosition: !isRestricted,
      del: !isRestricted,
      // Person → Add pregnancy; pregnancy → Edit + delete; donor → edit the parent
      // pregnancy. Shown for every node when the flag is on (the Add item is itself gated
      // on gestational capacity inside ReproductiveMenu).
      reproductive: config.ENABLE_REPRO_NODES
    }
  };
}
